import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import {
  CalendarDays,
  Clock,
  Car,
  Crown,
  Gift,
  Loader2,
  Plus,
  X,
  AlertCircle,
  Star,
  MessageSquare,
  Repeat,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, token, isPremium, loading: authLoading, refreshUser } = useAuth();
  const { t, language } = useLanguage();

  const [bookings, setBookings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  const locale = language === "de" ? de : enUS;

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login?redirect=/dashboard");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchData();
    }
  }, [isAuthenticated, token]);

  const fetchData = async () => {
    try {
      const [bookingsRes, offersRes, loyaltyRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/offers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/loyalty/points`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setBookings(bookingsRes.data);
      setOffers(offersRes.data);
      setLoyaltyPoints(loyaltyRes.data.points);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm(language === "de" ? "Möchten Sie diesen Termin wirklich stornieren?" : "Are you sure you want to cancel this appointment?")) {
      return;
    }

    try {
      await axios.delete(`${API}/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(language === "de" ? "Termin wurde storniert." : "Appointment cancelled.");
      fetchData();
    } catch (error) {
      toast.error(language === "de" ? "Stornierung fehlgeschlagen." : "Cancellation failed.");
    }
  };

  const handleOpenReview = (booking) => {
    setSelectedBooking(booking);
    setReviewRating(5);
    setReviewComment("");
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedBooking) return;
    
    setSubmittingReview(true);
    try {
      await axios.post(
        `${API}/reviews`,
        {
          booking_id: selectedBooking.id,
          service_id: selectedBooking.service_id,
          rating: reviewRating,
          comment: reviewComment || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(language === "de" ? "Bewertung abgegeben! +5 Punkte" : "Review submitted! +5 points");
      setShowReviewModal(false);
      fetchData();
      refreshUser();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to submit review";
      toast.error(message);
    } finally {
      setSubmittingReview(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const upcomingBookings = bookings.filter(
    (b) => b.status === "confirmed" && new Date(b.date) >= new Date()
  );
  const completedBookings = bookings.filter((b) => b.status === "completed");
  const pastBookings = bookings.filter(
    (b) => b.status !== "cancelled" && new Date(b.date) < new Date() && b.status !== "completed"
  );

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black text-white mb-2 font-['Chivo']">
                {t("dashboard_welcome")}, <span className="text-cyan-400">{user?.name}</span>
              </h1>
              <p className="text-zinc-400">
                {language === "de" ? "Verwalten Sie Ihre Termine und Mitgliedschaft." : "Manage your appointments and membership."}
              </p>
            </div>
            <Link to="/booking">
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium btn-glow" data-testid="new-booking-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t("dashboard_new_booking")}
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className={`p-4 rounded-sm ${isPremium ? "bg-cyan-500/10 border border-cyan-500/30" : "bg-zinc-900 border border-zinc-800"}`}>
            <div className="flex items-center gap-3">
              <Crown className={`h-5 w-5 ${isPremium ? "text-cyan-400" : "text-zinc-500"}`} />
              <div>
                <p className="text-xs text-zinc-500">{language === "de" ? "Mitgliedschaft" : "Membership"}</p>
                <p className="text-sm font-semibold text-white">{isPremium ? "Premium" : "Free"}</p>
              </div>
            </div>
          </div>
          
          <Link to="/loyalty" className="p-4 bg-zinc-900 border border-zinc-800 rounded-sm hover:border-cyan-500/50 transition-colors">
            <div className="flex items-center gap-3">
              <Star className="h-5 w-5 text-amber-400" />
              <div>
                <p className="text-xs text-zinc-500">{t("loyalty_points")}</p>
                <p className="text-sm font-semibold text-white">{loyaltyPoints}</p>
              </div>
            </div>
          </Link>
          
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="flex items-center gap-3">
              <CalendarDays className="h-5 w-5 text-green-400" />
              <div>
                <p className="text-xs text-zinc-500">{language === "de" ? "Anstehend" : "Upcoming"}</p>
                <p className="text-sm font-semibold text-white">{upcomingBookings.length}</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-5 w-5 text-purple-400" />
              <div>
                <p className="text-xs text-zinc-500">{language === "de" ? "Abgeschlossen" : "Completed"}</p>
                <p className="text-sm font-semibold text-white">{completedBookings.length}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Bookings Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Upcoming Bookings */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-cyan-400" />
                {t("dashboard_upcoming")}
              </h2>

              {upcomingBookings.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-zinc-500 mb-4">{t("dashboard_no_bookings")}</p>
                  <Link to="/booking">
                    <Button variant="outline" className="border-zinc-700" data-testid="book-now-empty-btn">
                      {t("dashboard_book_now")}
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="flex items-center justify-between p-4 bg-zinc-800/50 border border-zinc-700 rounded-sm"
                      data-testid={`booking-${booking.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-cyan-500/10 rounded-sm flex items-center justify-center">
                          <Car className="h-5 w-5 text-cyan-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-white">{booking.service_name}</h4>
                            {booking.is_recurring && (
                              <Repeat className="h-3 w-3 text-cyan-400" title="Recurring" />
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                            <span className="flex items-center gap-1">
                              <CalendarDays className="h-3 w-3" />
                              {format(new Date(booking.date), "dd. MMM yyyy", { locale })}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {booking.time_slot} Uhr
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelBooking(booking.id)}
                        className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                        data-testid={`cancel-booking-${booking.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Completed - Review Section */}
            {completedBookings.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-purple-400" />
                  {language === "de" ? "Bewertung abgeben" : "Leave a Review"}
                </h2>
                <div className="space-y-3">
                  {completedBookings.slice(0, 3).map((booking) => (
                    <div
                      key={booking.id}
                      className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-sm"
                    >
                      <div>
                        <h4 className="text-sm text-zinc-300">{booking.service_name}</h4>
                        <span className="text-xs text-zinc-500">
                          {format(new Date(booking.date), "dd. MMM yyyy", { locale })}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleOpenReview(booking)}
                        className="bg-purple-600 hover:bg-purple-500 text-white"
                        data-testid={`review-booking-${booking.id}`}
                      >
                        <Star className="h-3 w-3 mr-1" />
                        {t("reviews_write")}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Offers Sidebar */}
          <div className="space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Gift className="h-5 w-5 text-cyan-400" />
                {t("dashboard_offers")}
              </h2>

              {offers.length === 0 ? (
                <p className="text-zinc-500 text-sm">{language === "de" ? "Keine aktuellen Angebote." : "No current offers."}</p>
              ) : (
                <div className="space-y-4">
                  {offers.map((offer) => (
                    <div
                      key={offer.id}
                      className={`p-4 rounded-sm ${
                        offer.premium_only
                          ? "bg-cyan-500/10 border border-cyan-500/30"
                          : "bg-zinc-800/50 border border-zinc-700"
                      }`}
                      data-testid={`offer-${offer.id}`}
                    >
                      {offer.premium_only && (
                        <span className="text-xs text-cyan-400 font-medium mb-2 block">
                          PREMIUM EXKLUSIV
                        </span>
                      )}
                      <h4 className="text-sm font-medium text-white mb-1">
                        {language === "de" ? offer.title : offer.title_en}
                      </h4>
                      <p className="text-xs text-zinc-500 mb-2">
                        {language === "de" ? offer.description : offer.description_en}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-cyan-400">-{offer.discount}</span>
                        <span className="text-xs text-zinc-600">
                          {language === "de" ? "bis" : "until"} {offer.valid_until}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!isPremium && (
                <div className="mt-4 p-3 bg-zinc-800/50 rounded-sm flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-zinc-500">
                    {language === "de" 
                      ? "Als Premium-Mitglied erhalten Sie Zugang zu exklusiven Angeboten."
                      : "As a premium member, you get access to exclusive offers."}
                  </p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-semibold text-white mb-4">
                {language === "de" ? "Schnellzugriff" : "Quick Actions"}
              </h2>
              <div className="space-y-2">
                <Link to="/booking" className="block">
                  <Button variant="outline" className="w-full justify-start border-zinc-700 text-zinc-300 hover:text-white hover:border-cyan-500">
                    <CalendarDays className="h-4 w-4 mr-2" />
                    {t("nav_booking")}
                  </Button>
                </Link>
                <Link to="/loyalty" className="block">
                  <Button variant="outline" className="w-full justify-start border-zinc-700 text-zinc-300 hover:text-white hover:border-cyan-500">
                    <Star className="h-4 w-4 mr-2" />
                    {t("loyalty_title")}
                  </Button>
                </Link>
                <Link to="/services" className="block">
                  <Button variant="outline" className="w-full justify-start border-zinc-700 text-zinc-300 hover:text-white hover:border-cyan-500">
                    <Car className="h-4 w-4 mr-2" />
                    {t("nav_services")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">
              {t("reviews_write")}
            </h3>
            <p className="text-sm text-zinc-500 mb-4">{selectedBooking.service_name}</p>
            
            {/* Rating */}
            <div className="mb-4">
              <label className="block text-sm text-zinc-400 mb-2">{t("reviews_rating")}</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setReviewRating(star)}
                    className="p-1"
                  >
                    <Star
                      className={`h-8 w-8 ${star <= reviewRating ? "text-amber-400 fill-amber-400" : "text-zinc-600"}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            
            {/* Comment */}
            <div className="mb-6">
              <label className="block text-sm text-zinc-400 mb-2">{t("reviews_comment")}</label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder={language === "de" ? "Ihr Feedback..." : "Your feedback..."}
                className="w-full h-24 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-sm text-white placeholder-zinc-500 resize-none"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowReviewModal(false)}
                className="flex-1 border-zinc-700"
              >
                {t("common_cancel")}
              </Button>
              <Button
                onClick={handleSubmitReview}
                disabled={submittingReview}
                className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black"
              >
                {submittingReview ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t("reviews_submit")
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
